import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Search, MapPin, Briefcase, Filter, ArrowRight } from 'lucide-react';
import { jobListings } from '../mock';
import SEO from '../components/SEO';

const Careers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    resume: '',
    coverLetter: ''
  });

  const jobTypes = ['All', 'Permanent', 'Contract', 'Temporary', 'Executive'];
  const categories = ['All', 'Technology', 'Marketing', 'Finance', 'Human Resources', 'Operations', 'Sales'];

  const filteredJobs = jobListings.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'All' || job.type === selectedType;
    const matchesCategory = selectedCategory === 'All' || job.category === selectedCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  const handleApplicationSubmit = (e) => {
    e.preventDefault();
    // Store in localStorage for mock functionality
    const applications = JSON.parse(localStorage.getItem('applications') || '[]');
    applications.push({
      ...applicationData,
      jobId: selectedJob.id,
      jobTitle: selectedJob.title,
      submittedAt: new Date().toISOString()
    });
    localStorage.setItem('applications', JSON.stringify(applications));
    
    alert('Application submitted successfully! We will review your application and get back to you soon.');
    setSelectedJob(null);
    setApplicationData({
      name: '',
      email: '',
      phone: '',
      resume: '',
      coverLetter: ''
    });
  };

  return (
    <div>
      <SEO 
        title="Careers"
        description="Browse current job openings at Munoret. Find opportunities in technology, marketing, finance, HR, sales, operations, and more. Apply today to advance your career with top employers."
        keywords="job openings, career opportunities, employment, job search, technology jobs, marketing jobs, finance jobs, sales jobs, executive positions, Munoret careers"
      />
      {/* Hero Section */}
      <section style={{ 
        background: 'linear-gradient(180deg, var(--bg-light) 0%, var(--bg-primary) 100%)',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <motion.h1 
            className="display-hero"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '24px' }}
          >
            Find Your Next Opportunity
          </motion.h1>
          <motion.p 
            className="body-large"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}
          >
            Browse our current job openings and take the next step in your career.
          </motion.p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section style={{ padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'white',
              padding: '32px',
              borderRadius: '16px',
              border: '1px solid var(--border-medium)',
              marginBottom: '32px'
            }}
          >
            <div style={{ position: 'relative', marginBottom: '24px' }}>
              <Search 
                size={20} 
                style={{ 
                  position: 'absolute',
                  left: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} 
              />
              <input
                type="text"
                placeholder="Search jobs by title or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ paddingLeft: '52px' }}
              />
            </div>

            {/* Filters */}
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Filter size={20} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>Filters:</span>
              </div>
              
              <select 
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="form-input"
                style={{ width: 'auto', minWidth: '150px' }}
              >
                {jobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="form-input"
                style={{ width: 'auto', minWidth: '150px' }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              <div style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}>
                {filteredJobs.length} jobs found
              </div>
            </div>
          </motion.div>

          {/* Job Listings */}
          <div style={{ display: 'grid', gap: '24px' }}>
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -2 }}
                style={{
                  background: 'white',
                  padding: '32px',
                  borderRadius: '16px',
                  border: '1px solid var(--border-medium)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setSelectedJob(job)}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                  gap: '16px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 className="heading-medium" style={{ marginBottom: '8px' }}>
                      {job.title}
                    </h3>
                    {job.company !== "Confidential" && (
                      <div style={{ 
                        color: 'var(--text-secondary)',
                        marginBottom: '12px',
                        fontSize: '16px'
                      }}>
                        {job.company}
                      </div>
                    )}
                  </div>
                  <span style={{
                    background: 'var(--bg-light)',
                    color: 'var(--brand-primary)',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {job.type}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '24px',
                  flexWrap: 'wrap',
                  marginBottom: '16px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MapPin size={16} />
                    {job.location}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Briefcase size={16} />
                    {job.category}
                  </div>
                  <div style={{ fontWeight: '500', color: 'var(--brand-primary)' }}>
                    {job.salary}
                  </div>
                </div>

                <p style={{ 
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6',
                  marginBottom: '16px'
                }}>
                  {job.description}
                </p>

                <button className="btn-primary" style={{ gap: '8px' }}>
                  View Details & Apply
                  <ArrowRight size={18} />
                </button>
              </motion.div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '64px 24px',
              color: 'var(--text-secondary)'
            }}>
              <p className="body-large">No jobs found matching your criteria.</p>
              <p style={{ marginTop: '8px' }}>Try adjusting your filters or search term.</p>
            </div>
          )}
        </div>
      </section>

      {/* Job Detail Modal */}
      {selectedJob && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          overflowY: 'auto'
        }}
        onClick={() => setSelectedJob(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '48px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '32px' }}>
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <div>
                  <h2 className="heading-large" style={{ marginBottom: '8px' }}>
                    {selectedJob.title}
                  </h2>
                  {selectedJob.company !== "Confidential" && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>
                      {selectedJob.company}
                    </div>
                  )}
                </div>
                <span style={{
                  background: 'var(--brand-primary)',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {selectedJob.type}
                </span>
              </div>

              <div style={{
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap',
                color: 'var(--text-secondary)',
                paddingBottom: '24px',
                borderBottom: '1px solid var(--border-medium)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={18} />
                  {selectedJob.location}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Briefcase size={18} />
                  {selectedJob.category}
                </div>
                <div style={{ fontWeight: '600', color: 'var(--brand-primary)' }}>
                  {selectedJob.salary}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <h3 className="heading-medium" style={{ marginBottom: '16px' }}>
                Job Description
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {selectedJob.description}
              </p>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <h3 className="heading-medium" style={{ marginBottom: '16px' }}>
                Requirements
              </h3>
              <ul style={{ 
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {selectedJob.requirements.map((req, idx) => (
                  <li key={idx} style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    color: 'var(--text-secondary)'
                  }}>
                    <span style={{ 
                      color: 'var(--brand-primary)',
                      fontWeight: '600',
                      minWidth: '24px'
                    }}>
                      •
                    </span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            {/* Application Form */}
            <div style={{
              background: 'var(--bg-light)',
              padding: '32px',
              borderRadius: '16px',
              marginBottom: '24px'
            }}>
              <h3 className="heading-medium" style={{ marginBottom: '24px' }}>
                Apply for this Position
              </h3>
              <form onSubmit={handleApplicationSubmit}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <input
                    type="text"
                    placeholder="Full Name *"
                    required
                    value={applicationData.name}
                    onChange={(e) => setApplicationData({...applicationData, name: e.target.value})}
                    className="form-input"
                  />
                  <input
                    type="email"
                    placeholder="Email Address *"
                    required
                    value={applicationData.email}
                    onChange={(e) => setApplicationData({...applicationData, email: e.target.value})}
                    className="form-input"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number *"
                    required
                    value={applicationData.phone}
                    onChange={(e) => setApplicationData({...applicationData, phone: e.target.value})}
                    className="form-input"
                  />
                  <input
                    type="text"
                    placeholder="Resume URL or LinkedIn Profile *"
                    required
                    value={applicationData.resume}
                    onChange={(e) => setApplicationData({...applicationData, resume: e.target.value})}
                    className="form-input"
                  />
                  <textarea
                    placeholder="Cover Letter / Why are you interested in this role?"
                    value={applicationData.coverLetter}
                    onChange={(e) => setApplicationData({...applicationData, coverLetter: e.target.value})}
                    className="form-input"
                    rows="4"
                  />
                  <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                    <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                      Submit Application
                    </button>
                    <button 
                      type="button"
                      onClick={() => setSelectedJob(null)}
                      className="btn-secondary"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Careers;
